import React, { useState, useRef } from 'react';
import { formatDateOr } from '../utils/safeDate';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { brandColors, styles } from '../styles/clientManagementStyles';
import { shallowEqualIgnoringFunctions } from '../utils/memoCompare';
import BodyStatsSection from './bodystats/BodyStatsSection';

// The pending count covers the last 60 days only: a check-in left unreviewed for months is not "pending" any
// more and made the number look worse than it is (owner, 30 October 2026). Older ones are said, not counted.
export const PENDING_DAYS = 60;
export function pendingSummary(checkIns, coachNotes, now = Date.now()) {
  const cutoff = now - PENDING_DAYS * 86400000;
  const unreviewed = checkIns.filter(c => !(coachNotes || []).some(n => n.check_in_id === c.id));
  const recent = unreviewed.filter(c => new Date(c.check_in_date).getTime() >= cutoff).length;
  return { pending: recent, older: unreviewed.length - recent };
}

const ClientCheckInsTab = ({
  selectedClient,
  clientStartingWeight,
  clientOnboardingPhotos,
  clientWeightHistory,
  clientCoachNotes,
  clientCheckIns,
  sendCoachNote,
}) => {
  const [selectedPhotoComparison, setSelectedPhotoComparison] = useState(null);
  const [checkInNoteText, setCheckInNoteText] = useState('');
  const [sendingCheckInNote, setSendingCheckInNote] = useState(false);
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [editingReviewFor, setEditingReviewFor] = useState(null);
  const [sendingReviewFor, setSendingReviewFor] = useState(null);

  const latestReviewRef = useRef(null);
  const checkIns = (clientCheckIns || []).filter(c => !c.is_onboarding);
  const latestCheckIn = [...checkIns].sort((a, b) => new Date(b.check_in_date) - new Date(a.check_in_date))[0];
  const { pending: pendingReviews, older: olderUnreviewed } = pendingSummary(checkIns, clientCoachNotes);
  const olderNote = olderUnreviewed ? ` (${olderUnreviewed} older not counted)` : '';

  return (
    <div style={styles.section}>
      <div style={{ ...styles.card, padding: '20px', marginBottom: '20px' }}>
        <p role="status" style={{ color: brandColors.silver, margin: '0 0 12px' }}>
          {checkIns.length ? (pendingReviews ? pendingReviews + (pendingReviews === 1 ? ' check-in' : ' check-ins') + ' pending review' + olderNote : (olderUnreviewed ? 'All check-ins from the last 60 days reviewed' + olderNote : 'All check-ins reviewed')) : 'No monthly check-ins yet'}
        </p>
        {latestCheckIn && <button style={styles.viewBtn} onClick={() => {
          latestReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          latestReviewRef.current?.focus({ preventScroll: true });
        }}>Review latest check-in</button>}
      </div>
      {/* Body stats (plan 008): weight, body fat and muscle; add a scan for the client */}
      {selectedClient?.user_id && (
        <div>
          <BodyStatsSection clientId={selectedClient.user_id} viewer="coach" clientName={selectedClient.full_name || selectedClient.name || 'your client'}
            wide={typeof window !== 'undefined' && window.innerWidth >= 900} />
        </div>
      )}

      {/* Starting Point */}
      {(clientStartingWeight || clientOnboardingPhotos.length > 0) && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={styles.sectionTitle}>📸 Starting Point</h2>
          <div style={{ ...styles.card, border: `2px solid ${brandColors.gold}`, padding: '24px' }}>
            {clientStartingWeight && (
              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: clientOnboardingPhotos.length > 0 ? '20px' : '0' }}>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', color: brandColors.silver, display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    STARTING WEIGHT
                  </span>
                  <span style={{ fontSize: '28px', color: brandColors.gold, fontWeight: '700' }}>
                    {clientStartingWeight} kg
                  </span>
                </div>
                {clientWeightHistory.length > 0 && (
                  <>
                    <div style={{ textAlign: 'center', fontSize: '32px' }}>→</div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: '11px', color: brandColors.silver, display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        CURRENT WEIGHT
                      </span>
                      <span style={{ fontSize: '28px', color: brandColors.green, fontWeight: '700' }}>
                        {clientWeightHistory[clientWeightHistory.length - 1].weight} kg
                      </span>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: '11px', color: brandColors.silver, display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        CHANGE
                      </span>
                      <span style={{
                        fontSize: '28px',
                        color: (clientWeightHistory[clientWeightHistory.length - 1].weight - clientStartingWeight) < 0 ? brandColors.green : brandColors.gold,
                        fontWeight: '700'
                      }}>
                        {(clientWeightHistory[clientWeightHistory.length - 1].weight - clientStartingWeight) > 0 ? '+' : ''}
                        {(clientWeightHistory[clientWeightHistory.length - 1].weight - clientStartingWeight).toFixed(1)} kg
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {clientOnboardingPhotos.length > 0 && (
              <div>
                <span style={{ fontSize: '13px', color: brandColors.silver, display: 'block', marginBottom: '12px' }}>
                  📷 Onboarding Photos
                </span>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {clientOnboardingPhotos.map((photo) => (
                    <div key={photo.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <img
                        src={photo.photo_url}
                        alt={photo.notes}
                        style={{ width: '120px', height: '150px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: `2px solid ${brandColors.lightGray}` }}
                        onClick={() => setSelectedPhotoComparison({ url: photo.photo_url, title: photo.notes })}
                      />
                      <span style={{ fontSize: '11px', color: brandColors.silver, textAlign: 'center' }}>
                        {photo.notes?.replace('Onboarding ', '').replace(' photo', '') || 'Photo'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Weight Progress Chart */}
      {(clientWeightHistory.length > 0 || clientStartingWeight) && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={styles.sectionTitle}>⚖️ Weight Progress Over Time</h2>
          <div style={styles.card}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={(() => {
                  const chartData = [];

                  // Add starting weight
                  if (clientStartingWeight) {
                    const startDate = clientOnboardingPhotos.length > 0
                      ? new Date(clientOnboardingPhotos[0].created_at)
                      : clientWeightHistory.length > 0
                        ? new Date(new Date(clientWeightHistory[0].date).getTime() - 30 * 24 * 60 * 60 * 1000)
                        : new Date();

                    chartData.push({
                      date: startDate.toLocaleDateString(),
                      weight: clientStartingWeight,
                      label: 'Start'
                    });
                  }

                  // Add check-in weights
                  clientWeightHistory.forEach(entry => {
                    chartData.push({
                      date: new Date(entry.date).toLocaleDateString(),
                      weight: entry.weight,
                      label: `Week ${entry.week}`
                    });
                  });

                  return chartData;
                })()}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={brandColors.lightGray} />
                <XAxis dataKey="date" stroke={brandColors.silver} tick={{ fill: brandColors.silver, fontSize: 11 }} />
                <YAxis stroke={brandColors.silver} tick={{ fill: brandColors.silver, fontSize: 12 }} domain={['dataMin - 2', 'dataMax + 2']} />
                <Tooltip
                  contentStyle={{ backgroundColor: brandColors.mediumGray, border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px' }}
                  labelStyle={{ color: brandColors.white }}
                  itemStyle={{ color: brandColors.gold }}
                />
                <Line type="monotone" dataKey="weight" stroke={brandColors.gold} strokeWidth={3} dot={{ fill: brandColors.gold, r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Write a Note */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={styles.sectionTitle}>✍️ Write a Note</h2>
        <div style={{ ...styles.card, padding: '20px' }}>
          <p style={{ color: brandColors.silver, margin: '0 0 4px', fontSize: '12px' }}>
            Shows on {selectedClient?.full_name || 'the client'}'s workout screen the next time they open the app, with a push notification. To answer a check-in, write a review under it in Check-In History below
          </p>
          <p style={{ color: brandColors.gold, margin: '0 0 12px', fontSize: '12px' }}>
            {clientCoachNotes.length > 0
              ? `📨 Last note sent ${new Date(clientCoachNotes[0].created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${new Date(clientCoachNotes[0].created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} (${clientCoachNotes.length} total)`
              : 'No notes sent yet'}
          </p>
          <textarea
            value={checkInNoteText}
            onChange={(e) => setCheckInNoteText(e.target.value)}
            placeholder="Reply to their check-in, or just say hi..."
            rows={3}
            style={{
              width: '100%', padding: '12px', backgroundColor: brandColors.darkGray,
              border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px',
              color: brandColors.white, fontSize: '14px', lineHeight: 1.5,
              fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: '12px',
            }}
          />
          <button
            onClick={() => sendCoachNote(checkInNoteText, {
              setSending: setSendingCheckInNote,
              onSuccess: () => setCheckInNoteText(''),
            })}
            disabled={!checkInNoteText.trim() || sendingCheckInNote}
            style={{
              padding: '10px 20px', borderRadius: '8px', border: 'none',
              backgroundColor: checkInNoteText.trim() && !sendingCheckInNote ? brandColors.gold : brandColors.lightGray,
              color: checkInNoteText.trim() && !sendingCheckInNote ? brandColors.darkGray : brandColors.silver,
              fontSize: '13px', fontWeight: '700',
              cursor: checkInNoteText.trim() && !sendingCheckInNote ? 'pointer' : 'not-allowed',
            }}
          >
            {sendingCheckInNote ? 'Sending...' : 'Send Note'}
          </button>
        </div>
      </div>

      {/* Check-Ins History */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={styles.sectionTitle}>📋 Check-In History</h2>
        {clientCheckIns && clientCheckIns.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {clientCheckIns.map((checkIn) => (
              <div key={checkIn.id} ref={checkIn.id === latestCheckIn?.id ? latestReviewRef : null} tabIndex={-1} aria-label="Monthly check-in review" style={{ ...styles.card, padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: brandColors.white, marginBottom: '4px' }}>
                      {formatDateOr(checkIn.check_in_date, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                    {checkIn.week_number != null && (
                      <div style={{ fontSize: '13px', color: brandColors.silver }}>
                        Week {checkIn.week_number}
                      </div>
                    )}
                  </div>
                  {checkIn.bodyweight_kg != null && (
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: '700', color: brandColors.green, gap: '4px' }}>
                      ⚖️ {checkIn.bodyweight_kg} kg
                    </div>
                  )}
                </div>

                {checkIn.message_to_coach && (
                  <div style={{ padding: '12px', backgroundColor: brandColors.mediumGray, borderRadius: '8px', fontSize: '14px', color: brandColors.white, marginBottom: '12px', lineHeight: '1.5' }}>
                    💬 {checkIn.message_to_coach}
                  </div>
                )}

                {checkIn.photo_urls && checkIn.photo_urls.length > 0 && (
                  <div>
                    <div style={{ fontSize: '13px', color: brandColors.silver, marginBottom: '8px' }}>
                      🖼️ {checkIn.photo_urls.length} {checkIn.photo_urls.length === 1 ? 'Photo' : 'Photos'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {checkIn.photo_urls.map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt={`Progress ${idx + 1}`}
                          style={{ width: '100px', height: '125px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: `2px solid ${brandColors.lightGray}` }}
                          onClick={() => setSelectedPhotoComparison({ url, title: `Week ${checkIn.week_number} - ${new Date(checkIn.check_in_date).toLocaleDateString()}` })}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {!checkIn.is_onboarding && (() => {
                  const existingReview = (clientCoachNotes || []).find(n => n.check_in_id === checkIn.id);
                  const isEditing = editingReviewFor === checkIn.id;
                  const isSending = sendingReviewFor === checkIn.id;

                  if (existingReview && !isEditing) {
                    return (
                      <div style={{ marginTop: '12px', padding: '12px', backgroundColor: brandColors.mediumGray, borderRadius: '8px', border: `1px solid ${brandColors.gold}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '11px', color: brandColors.gold, fontWeight: '700', letterSpacing: '0.04em' }}>
                            ⭐ COACH REVIEW
                          </span>
                          <button
                            onClick={() => {
                              setReviewDrafts(prev => ({ ...prev, [checkIn.id]: existingReview.message }));
                              setEditingReviewFor(checkIn.id);
                            }}
                            style={{ background: 'none', border: 'none', color: brandColors.silver, fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            Edit
                          </button>
                        </div>
                        <div style={{ fontSize: '14px', color: brandColors.white, lineHeight: 1.5 }}>
                          {existingReview.message}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div style={{ marginTop: '12px' }}>
                      <textarea
                        value={reviewDrafts[checkIn.id] ?? ''}
                        onChange={(e) => setReviewDrafts(prev => ({ ...prev, [checkIn.id]: e.target.value }))}
                        placeholder="Write a structured review for this check-in..."
                        rows={2}
                        style={{
                          width: '100%', padding: '10px', backgroundColor: brandColors.darkGray,
                          border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px',
                          color: brandColors.white, fontSize: '13px', lineHeight: 1.5,
                          fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px',
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => sendCoachNote(reviewDrafts[checkIn.id], {
                            checkInId: checkIn.id,
                            setSending: (v) => setSendingReviewFor(v ? checkIn.id : null),
                            onSuccess: () => {
                              setReviewDrafts(prev => ({ ...prev, [checkIn.id]: '' }));
                              setEditingReviewFor(null);
                            },
                          })}
                          disabled={!(reviewDrafts[checkIn.id] || '').trim() || isSending}
                          style={{
                            padding: '8px 16px', borderRadius: '8px', border: 'none',
                            backgroundColor: (reviewDrafts[checkIn.id] || '').trim() && !isSending ? brandColors.gold : brandColors.lightGray,
                            color: (reviewDrafts[checkIn.id] || '').trim() && !isSending ? brandColors.darkGray : brandColors.silver,
                            fontSize: '12px', fontWeight: '700',
                            cursor: (reviewDrafts[checkIn.id] || '').trim() && !isSending ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {isSending ? 'Saving...' : existingReview ? 'Save Review' : 'Write Review'}
                        </button>
                        {isEditing && (
                          <button
                            onClick={() => setEditingReviewFor(null)}
                            style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${brandColors.lightGray}`, background: 'none', color: brandColors.silver, fontSize: '12px', cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.noProgram}>
            <span style={{ fontSize: '48px' }}>📅</span>
            <p>No check-ins submitted yet</p>
          </div>
        )}
      </div>

      {/* Photo Comparison Modal - Only in Check-ins Tab */}
      {selectedPhotoComparison && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setSelectedPhotoComparison(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: brandColors.white, marginBottom: '16px', textAlign: 'center' }}>
              {selectedPhotoComparison.title}
            </h3>
            <img
              src={selectedPhotoComparison.url}
              alt="Progress"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '12px' }}
            />
            <button
              onClick={() => setSelectedPhotoComparison(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: brandColors.red,
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                color: brandColors.white,
                fontSize: '24px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ClientCheckInsTab, shallowEqualIgnoringFunctions);
